// ./src/components/TranscriptDisplay.jsx
import React, { useState } from "react";
import { Copy, FileText, CheckCircle, Sparkles, Eye, Zap } from "lucide-react";

const TranscriptDisplay = ({ transcript, isProcessing }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(transcript);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <div className="card-premium p-8 animate-fadeInUp">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shadow-color">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gradient">Transkript</h2>
            <p className="text-neutral-500 text-sm">
              KI-generierte Verschriftlichung
            </p>
          </div>
        </div>

        {transcript && (
          <button onClick={copyToClipboard} className="btn-secondary group">
            {copied ? (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span className="text-emerald-600 font-medium">Kopiert!</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 group-hover:animate-pulse" />
                <span>Kopieren</span>
              </>
            )}
          </button>
        )}
      </div>

      {isProcessing ? (
        <div className="flex flex-col items-center justify-center py-16 space-y-6">
          {/* Moderne Loading Animation */}
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-3xl flex items-center justify-center animate-pulse-glow">
              <Zap className="w-10 h-10 text-primary-600 animate-float" />
            </div>
            <div className="absolute -inset-1 bg-gradient-to-r from-primary-600 to-primary-400 rounded-3xl blur opacity-30 animate-pulse"></div>
          </div>

          <div className="text-center space-y-3">
            <h3 className="text-xl font-bold text-neutral-800">
              KI transkribiert dein Meeting
            </h3>
            <p className="text-neutral-600 max-w-md">
              Unsere fortschrittliche KI wandelt gerade deine Aufnahme in Text
              um. Das dauert normalerweise 2-5 Minuten.
            </p>
          </div>

          {/* Processing Steps */}
          <div className="w-full max-w-md space-y-4">
            <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-emerald-50 to-emerald-100 rounded-2xl border border-emerald-200">
              <div className="w-8 h-8 bg-emerald-500 rounded-xl flex items-center justify-center animate-pulse">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-emerald-800">Audio analysiert</p>
                <p className="text-sm text-emerald-600">
                  Sprache erkannt und bereinigt
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl border border-orange-200">
              <div className="w-8 h-8 bg-orange-500 rounded-xl flex items-center justify-center animate-spin">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-orange-800">
                  Transkription läuft...
                </p>
                <p className="text-sm text-orange-600">
                  KI konvertiert Sprache zu Text
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4 p-4 bg-gradient-to-r from-neutral-50 to-neutral-100 rounded-2xl border border-neutral-200 opacity-50">
              <div className="w-8 h-8 bg-neutral-300 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-neutral-500" />
              </div>
              <div>
                <p className="font-medium text-neutral-600">
                  Optimierung & Format
                </p>
                <p className="text-sm text-neutral-500">
                  Text wird aufbereitet
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : transcript ? (
        <div className="space-y-6">
          {/* Transcript Content */}
          <div className="relative">
            <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl p-6 max-h-96 overflow-y-auto border border-neutral-200 hover-lift">
              <div className="absolute top-4 right-4 opacity-20">
                <Eye className="w-5 h-5 text-neutral-400" />
              </div>
              <p className="text-neutral-700 leading-relaxed whitespace-pre-wrap font-medium">
                {transcript}
              </p>
            </div>
          </div>

          {/* Transcript Stats & Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="card-modern p-4 text-center hover-lift">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-neutral-600">Wörter</p>
              <p className="text-xl font-bold text-neutral-800">
                {transcript.split(" ").length}
              </p>
            </div>

            <div className="card-modern p-4 text-center hover-lift">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Zap className="w-4 h-4 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-neutral-600">Zeichen</p>
              <p className="text-xl font-bold text-neutral-800">
                {transcript.length}
              </p>
            </div>

            <div className="card-modern p-4 text-center hover-lift">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Sparkles className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-neutral-600">Lesedauer</p>
              <p className="text-xl font-bold text-neutral-800">
                {Math.ceil(transcript.split(" ").length / 200)} min
              </p>
            </div>
          </div>

          {/* Success Message */}
          <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 border border-emerald-200 rounded-2xl p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-color">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-emerald-900 text-lg">
                  Transkription erfolgreich!
                </h4>
                <p className="text-emerald-700">
                  Du kannst den Text jetzt kopieren, herunterladen oder für
                  weitere Analysen verwenden.
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Empty State */
        <div className="text-center py-16 space-y-6">
          <div className="relative">
            <div className="w-24 h-24 bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-3xl flex items-center justify-center mx-auto">
              <FileText className="w-12 h-12 text-neutral-400" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-xl font-bold text-neutral-600">
              Hier erscheint dein Transkript
            </h3>
            <p className="text-neutral-500 max-w-md mx-auto">
              Lade eine Meeting-Aufzeichnung hoch und unsere KI erstellt
              automatisch eine präzise Verschriftlichung für dich.
            </p>
          </div>

          {/* Feature Preview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-lg mx-auto mt-8">
            <div className="text-center p-4">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Zap className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-sm font-medium text-neutral-600">
                Blitzschnell
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Eye className="w-5 h-5 text-emerald-600" />
              </div>
              <p className="text-sm font-medium text-neutral-600">Präzise</p>
            </div>

            <div className="text-center p-4">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                <Sparkles className="w-5 h-5 text-cyan-600" />
              </div>
              <p className="text-sm font-medium text-neutral-600">KI-Powered</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptDisplay;
